export interface Quotation {
  id: string;
  subject: string;
  received: string;
  status: 'New' | 'Acknowledged' | 'Quoted' | 'Won' | 'Lost';
  customerName: string;
  company: string;
  serviceType: 'Air Freight' | 'Ocean' | 'Air' | 'Air Freight, Import';
  origin: string;
  destination: string;
  cargoDescription: string;
  weight: string;
  dimensions: string;
  specialRequirements: string;
  quotedRate?: number;
  quotedTime?: string;
  followUpDate?: string;
  priority: 'High' | 'Medium' | 'Low';
  assignedTo: string;
  estimatedValue?: number;
  channel: 'Email' | 'WhatsApp' | 'Phone' | 'Web';
  attachments?: string[];
  aiSummary: {
    leadName: string;
    requirements: string;
    urgency: 'High' | 'Medium' | 'Low';
    extractedFields: {
      product: string;
      quantity: string;
      incoterms: string;
      timeline: string;
    };
  };
  revisions?: QuotationRevision[];
  emailThread?: EmailThread[];
}

export interface QuotationRevision {
  id: string;
  timestamp: string;
  changes: string[];
  updatedBy: string;
  version: number;
}

export interface EmailThread {
  id: string;
  timestamp: string;
  sender: string;
  recipient: string;
  subject: string;
  content: string;
  type: 'incoming' | 'outgoing' | 'internal';
  attachments?: string[];
  status: 'read' | 'unread' | 'replied';
  priority: 'High' | 'Medium' | 'Low';
  tags?: string[];
  aiExtractedInfo?: {
    keyPoints: string[];
    actionItems: string[];
    sentiment: 'positive' | 'neutral' | 'negative';
    urgency: 'High' | 'Medium' | 'Low';
  };
}

export interface DashboardMetrics {
  totalQuotations: number;
  newQuotations: number;
  quotedQuotations: number;
  wonQuotations: number;
  conversionRate: number;
  avgResponseTime: string;
  totalValue: number;
  topChannels: { channel: string; count: number }[];
  recentActivity: { id: string; action: string; timestamp: string }[];
}

export const dummyQuotations: Quotation[] = [
  {
    id: 'Q-001',
    subject: 'KORAY // KK549 // INDIA TO IST // EXW AIR',
    received: '2025-10-22T13:18:56',
    status: 'New',
    customerName: 'Koray Konuk',
    company: 'GALATA TAŞIMACILIK VE TİCARET A.Ş.',
    serviceType: 'Air Freight',
    origin: 'Ahmedabad, Gujarat India',
    destination: 'Izmir, Turkey',
    cargoDescription: '67 cases (no further description)',
    weight: '19219 kgs',
    dimensions: 'Not specified',
    specialRequirements: 'EXW (Ex Works) incoterm requested',
    priority: 'Medium',
    assignedTo: 'Prashant Pathil',
    channel: 'Email',
    attachments: ['packing-list.pdf', 'invoice.pdf'],
    aiSummary: {
      leadName: 'Koray Konuk',
      requirements: 'Air freight from Ahmedabad to Izmir, Turkey for 67 cases weighing 19,219 kg',
      urgency: 'Medium',
      extractedFields: {
        product: 'General Cargo (67 cases)',
        quantity: '67 cases',
        incoterms: 'EXW',
        timeline: 'Standard delivery'
      }
    },
    emailThread: [
      {
        id: 'ET-001-1',
        timestamp: '2025-10-22T13:18:56',
        sender: 'Koray Konuk <koray.konuk@galata.com.tr>',
        recipient: 'Prashant Pathil <prashant@galific.com>',
        subject: 'KORAY // KK549 // INDIA TO IST // EXW AIR',
        content: 'Dear Prashant,\n\nI hope this email finds you well. We have an urgent shipment requirement from Ahmedabad, Gujarat India to Izmir, Turkey.\n\nShipment Details:\n- 67 cases\n- Weight: 19,219 kgs\n- Incoterm: EXW (Ex Works)\n- Service: Air Freight\n- Reference: KK549\n\nCould you please provide us with:\n1. Competitive air freight rates\n2. Transit time\n3. Documentation requirements\n4. Any special handling considerations\n\nWe need this quotation by tomorrow morning as our client is waiting for pricing. Please let me know if you need any additional information.\n\nBest regards,\nKoray Konuk\nGalata Taşımacılık ve Ticaret A.Ş.',
        type: 'incoming',
        attachments: ['packing-list.pdf', 'invoice.pdf'],
        status: 'read',
        priority: 'High',
        tags: ['urgent', 'air-freight', 'exw'],
        aiExtractedInfo: {
          keyPoints: ['67 cases shipment', '19,219 kgs weight', 'EXW incoterm', 'Ahmedabad to Izmir', 'Urgent pricing needed'],
          actionItems: ['Provide air freight rates', 'Calculate transit time', 'List documentation requirements', 'Check special handling needs'],
          sentiment: 'neutral',
          urgency: 'High'
        }
      },
      {
        id: 'ET-001-2',
        timestamp: '2025-10-22T14:30:15',
        sender: 'Prashant Pathil <prashant@galific.com>',
        recipient: 'Koray Konuk <koray.konuk@galata.com.tr>',
        subject: 'RE: KORAY // KK549 // INDIA TO IST // EXW AIR',
        content: 'Dear Koray,\n\nThank you for your inquiry. I have reviewed your shipment details and here are our findings:\n\nPreliminary Information:\n- Route: Ahmedabad (AMD) to Izmir (ADB)\n- Weight: 19,219 kgs (approximately 19.2 tons)\n- Service: Air Freight\n- Incoterm: EXW\n\nI need to clarify a few details:\n1. What is the nature of the cargo? (General cargo, machinery, textiles, etc.)\n2. Do you have the exact dimensions of each case?\n3. Any special handling requirements?\n4. Preferred departure date?\n\nI will get back to you with detailed pricing within 2 hours once I have this information.\n\nBest regards,\nPrashant Pathil\nGalific Logistics',
        type: 'outgoing',
        status: 'read',
        priority: 'High',
        tags: ['follow-up', 'clarification-needed'],
        aiExtractedInfo: {
          keyPoints: ['Route confirmed', 'Weight verified', 'Additional information needed', '2-hour response promise'],
          actionItems: ['Get cargo details', 'Obtain dimensions', 'Check handling requirements', 'Confirm departure date'],
          sentiment: 'positive',
          urgency: 'High'
        }
      },
      {
        id: 'ET-001-3',
        timestamp: '2025-10-22T15:45:30',
        sender: 'Koray Konuk <koray.konuk@galata.com.tr>',
        recipient: 'Prashant Pathil <prashant@galific.com>',
        subject: 'RE: KORAY // KK549 // INDIA TO IST // EXW AIR - Additional Info',
        content: 'Dear Prashant,\n\nThank you for your quick response. Here are the additional details:\n\nCargo Information:\n- Nature: Textile machinery parts\n- Dimensions: Each case approximately 120cm x 80cm x 60cm\n- Special handling: Fragile items, handle with care\n- Preferred departure: Next Tuesday (October 28th)\n\nAdditional Requirements:\n- Insurance coverage required\n- Temperature-controlled storage if needed\n- Customs clearance assistance\n\nPlease provide:\n1. All-inclusive pricing\n2. Transit time (door-to-door)\n3. Insurance options\n4. Customs documentation list\n\nLooking forward to your detailed quotation.\n\nBest regards,\nKoray Konuk',
        type: 'incoming',
        attachments: ['technical-specs.pdf', 'insurance-requirements.pdf'],
        status: 'read',
        priority: 'High',
        tags: ['machinery', 'fragile', 'insurance', 'customs'],
        aiExtractedInfo: {
          keyPoints: ['Textile machinery parts', 'Fragile handling required', 'Insurance needed', 'Customs assistance required'],
          actionItems: ['Calculate fragile cargo rates', 'Arrange insurance quotes', 'Prepare customs documentation', 'Confirm temperature requirements'],
          sentiment: 'neutral',
          urgency: 'High'
        }
      },
      {
        id: 'ET-001-4',
        timestamp: '2025-10-22T17:20:45',
        sender: 'Prashant Pathil <prashant@galific.com>',
        recipient: 'Koray Konuk <koray.konuk@galata.com.tr>',
        subject: 'RE: KORAY // KK549 // INDIA TO IST // EXW AIR - Quotation',
        content: 'Dear Koray,\n\nThank you for the detailed information. Please find our comprehensive quotation below:\n\nQUOTATION SUMMARY:\n- Route: Ahmedabad (AMD) to Izmir (ADB)\n- Cargo: 67 cases, Textile machinery parts\n- Weight: 19,219 kgs\n- Service: Air Freight (EXW)\n\nPRICING:\n- Air freight: $4.50/kg = $86,485.50\n- Fragile handling surcharge: $2,000\n- Insurance (0.3% of cargo value): $1,200\n- Customs clearance: $500\n- Documentation: $200\n\nTOTAL COST: $90,385.50\n\nTRANSIT TIME:\n- Collection: 1 day\n- Air transit: 2 days\n- Customs clearance: 1 day\n- Delivery: 1 day\n- Total: 5 days door-to-door\n\nVALIDITY: 7 days\n\nPlease confirm if this meets your requirements.\n\nBest regards,\nPrashant Pathil',
        type: 'outgoing',
        attachments: ['quotation-kk549.pdf', 'terms-conditions.pdf'],
        status: 'read',
        priority: 'High',
        tags: ['quotation-sent', 'pricing', 'transit-time'],
        aiExtractedInfo: {
          keyPoints: ['Total cost: $90,385.50', '5-day transit time', '7-day validity', 'Comprehensive pricing breakdown'],
          actionItems: ['Review quotation', 'Confirm acceptance', 'Arrange payment terms', 'Schedule collection'],
          sentiment: 'positive',
          urgency: 'Medium'
        }
      }
    ]
  },
  {
    id: 'Q-002',
    subject: 'ERENSOY/O.A.I.25.004558/BOM TO IST/FOB/BY AIR (IMP)',
    received: '2025-10-21T19:56:57',
    status: 'New',
    customerName: 'N/A',
    company: 'N/A',
    serviceType: 'Air',
    origin: 'BOM',
    destination: 'IST',
    cargoDescription: '1 PALLET',
    weight: '310 KGS',
    dimensions: '91 x 79 x 60cm',
    specialRequirements: 'FOB rates',
    priority: 'Medium',
    assignedTo: 'Prashant Pathil',
    channel: 'Email',
    aiSummary: {
      leadName: 'ERENSOY Customer',
      requirements: 'Air freight from Mumbai to Istanbul for 1 pallet weighing 310 kg',
      urgency: 'Medium',
      extractedFields: {
        product: 'General Cargo (1 pallet)',
        quantity: '1 pallet',
        incoterms: 'FOB',
        timeline: 'Standard delivery'
      }
    },
    emailThread: [
      {
        id: 'ET-002-1',
        timestamp: '2025-10-21T19:56:57',
        sender: 'Erensoy Logistics <info@erensoy.com.tr>',
        recipient: 'Prashant Pathil <prashant@galific.com>',
        subject: 'ERENSOY/O.A.I.25.004558/BOM TO IST/FOB/BY AIR (IMP)',
        content: 'Dear Prashant,\n\nWe have a shipment requirement from Mumbai (BOM) to Istanbul (IST).\n\nShipment Details:\n- Reference: O.A.I.25.004558\n- Cargo: 1 Pallet\n- Weight: 310 KGS\n- Dimensions: 91 x 79 x 60cm\n- Incoterm: FOB\n- Service: Air Freight (Import)\n\nPlease provide:\n1. FOB rates from Mumbai\n2. Transit time\n3. Documentation requirements\n4. Any additional charges\n\nThis is for our regular client, so please provide competitive rates.\n\nBest regards,\nErensoy Logistics Team',
        type: 'incoming',
        status: 'read',
        priority: 'Medium',
        tags: ['fob', 'air-freight', 'import', 'regular-client'],
        aiExtractedInfo: {
          keyPoints: ['1 pallet shipment', '310 kgs weight', 'FOB incoterm', 'Mumbai to Istanbul', 'Regular client'],
          actionItems: ['Calculate FOB rates', 'Determine transit time', 'List documentation', 'Check additional charges'],
          sentiment: 'neutral',
          urgency: 'Medium'
        }
      },
      {
        id: 'ET-002-2',
        timestamp: '2025-10-21T21:15:30',
        sender: 'Prashant Pathil <prashant@galific.com>',
        recipient: 'Erensoy Logistics <info@erensoy.com.tr>',
        subject: 'RE: ERENSOY/O.A.I.25.004558/BOM TO IST/FOB/BY AIR (IMP)',
        content: 'Dear Erensoy Team,\n\nThank you for your inquiry. Here are our rates for your shipment:\n\nSHIPMENT DETAILS:\n- Route: Mumbai (BOM) to Istanbul (IST)\n- Cargo: 1 Pallet\n- Weight: 310 KGS\n- Dimensions: 91 x 79 x 60cm\n\nFOB RATES:\n- Air freight: $3.80/kg = $1,178.00\n- Handling charges: $150\n- Documentation: $100\n- Terminal charges: $75\n\nTOTAL FOB COST: $1,503.00\n\nTRANSIT TIME: 3-4 days\nDOCUMENTATION: Commercial invoice, packing list, airway bill\n\nPlease confirm if these rates are acceptable.\n\nBest regards,\nPrashant Pathil',
        type: 'outgoing',
        status: 'read',
        priority: 'Medium',
        tags: ['quotation-sent', 'fob-pricing'],
        aiExtractedInfo: {
          keyPoints: ['Total FOB cost: $1,503.00', '3-4 day transit', 'Standard documentation', 'Competitive rates offered'],
          actionItems: ['Review quotation', 'Confirm acceptance', 'Arrange booking', 'Prepare documentation'],
          sentiment: 'positive',
          urgency: 'Medium'
        }
      }
    ]
  },
  {
    id: 'Q-003',
    subject: 'EXW / IST / #PRI17628',
    received: '2025-10-21T17:20:04',
    status: 'New',
    customerName: 'Emre BIRINCI',
    company: 'BATI NAKLIYAT VE TICARET A.S.',
    serviceType: 'Air',
    origin: 'Goa 403 722, India',
    destination: 'Istanbul (IST), Turkey',
    cargoDescription: 'General Cargo',
    weight: 'G.W: 572.56 kg, N.W: 519.54 kg',
    dimensions: '1 piece, 100 cm x 100 cm x 123 cm',
    specialRequirements: 'EXW (Ex Works) terms requested. AOL (Airport of Loading) advice requested.',
    priority: 'Medium',
    assignedTo: 'Prashant Pathil',
    channel: 'Email',
    attachments: ['packing-list.pdf'],
    aiSummary: {
      leadName: 'Emre BIRINCI',
      requirements: 'Air freight from Goa to Istanbul for general cargo weighing 572.56 kg',
      urgency: 'Medium',
      extractedFields: {
        product: 'General Cargo',
        quantity: '1 piece',
        incoterms: 'EXW',
        timeline: 'Standard delivery'
      }
    },
    emailThread: [
      {
        id: 'ET-003-1',
        timestamp: '2025-10-21T17:20:04',
        sender: 'Emre BIRINCI <emre.birinci@batinakliyat.com.tr>',
        recipient: 'Prashant Pathil <prashant@galific.com>',
        subject: 'EXW / IST / #PRI17628',
        content: 'Dear Prashant,\n\nI hope you are doing well. We have a shipment requirement from Goa, India to Istanbul, Turkey.\n\nShipment Details:\n- Reference: #PRI17628\n- Origin: Goa 403 722, India\n- Destination: Istanbul (IST), Turkey\n- Cargo: General Cargo\n- Weight: G.W: 572.56 kg, N.W: 519.54 kg\n- Dimensions: 1 piece, 100 cm x 100 cm x 123 cm\n- Incoterm: EXW (Ex Works)\n\nSpecial Requirements:\n- AOL (Airport of Loading) advice requested\n- EXW terms\n- General cargo handling\n\nPlease provide:\n1. EXW rates from Goa\n2. Transit time to Istanbul\n3. Airport of loading information\n4. Documentation requirements\n\nLooking forward to your competitive quotation.\n\nBest regards,\nEmre BIRINCI\nBatı Nakliyat ve Ticaret A.Ş.',
        type: 'incoming',
        attachments: ['packing-list.pdf'],
        status: 'read',
        priority: 'Medium',
        tags: ['exw', 'goa', 'istanbul', 'general-cargo'],
        aiExtractedInfo: {
          keyPoints: ['572.56 kg general cargo', 'Goa to Istanbul', 'EXW incoterm', 'AOL advice needed'],
          actionItems: ['Calculate EXW rates', 'Provide AOL information', 'Determine transit time', 'List documentation'],
          sentiment: 'neutral',
          urgency: 'Medium'
        }
      },
      {
        id: 'ET-003-2',
        timestamp: '2025-10-21T18:45:20',
        sender: 'Prashant Pathil <prashant@galific.com>',
        recipient: 'Emre BIRINCI <emre.birinci@batinakliyat.com.tr>',
        subject: 'RE: EXW / IST / #PRI17628',
        content: 'Dear Emre,\n\nThank you for your inquiry. Here are the details for your shipment:\n\nSHIPMENT SUMMARY:\n- Reference: #PRI17628\n- Route: Goa (GOI) to Istanbul (IST)\n- Cargo: General Cargo\n- Weight: 572.56 kg (Gross), 519.54 kg (Net)\n- Dimensions: 100 x 100 x 123 cm\n\nEXW PRICING:\n- Air freight: $3.50/kg = $2,003.96\n- Handling charges: $200\n- Documentation: $150\n- Terminal charges: $100\n\nTOTAL EXW COST: $2,453.96\n\nAIRPORT OF LOADING:\n- Goa International Airport (GOI)\n- Terminal: Cargo Terminal 1\n- Operating hours: 24/7\n\nTRANSIT TIME: 4-5 days\nDOCUMENTATION: Commercial invoice, packing list, airway bill, export declaration\n\nPlease confirm if this meets your requirements.\n\nBest regards,\nPrashant Pathil',
        type: 'outgoing',
        status: 'read',
        priority: 'Medium',
        tags: ['quotation-sent', 'exw-pricing', 'aol-info'],
        aiExtractedInfo: {
          keyPoints: ['Total EXW cost: $2,453.96', 'Goa Airport (GOI)', '4-5 day transit', '24/7 operations'],
          actionItems: ['Review quotation', 'Confirm acceptance', 'Arrange collection', 'Prepare export docs'],
          sentiment: 'positive',
          urgency: 'Medium'
        }
      },
      {
        id: 'ET-003-3',
        timestamp: '2025-10-21T20:30:15',
        sender: 'Emre BIRINCI <emre.birinci@batinakliyat.com.tr>',
        recipient: 'Prashant Pathil <prashant@galific.com>',
        subject: 'RE: EXW / IST / #PRI17628 - Confirmation',
        content: 'Dear Prashant,\n\nThank you for the detailed quotation. The rates look competitive.\n\nI have a few questions:\n1. Can you provide door-to-door service from Goa?\n2. What are the insurance options available?\n3. Do you handle customs clearance in Istanbul?\n4. What is the validity period of this quotation?\n\nAlso, our client is asking about the possibility of consolidating this shipment with other cargo going to Istanbul. Do you have any consolidation services?\n\nPlease let me know at your earliest convenience.\n\nBest regards,\nEmre BIRINCI',
        type: 'incoming',
        status: 'read',
        priority: 'Medium',
        tags: ['door-to-door', 'insurance', 'customs', 'consolidation'],
        aiExtractedInfo: {
          keyPoints: ['Door-to-door service inquiry', 'Insurance options needed', 'Customs clearance question', 'Consolidation possibility'],
          actionItems: ['Check door-to-door rates', 'Prepare insurance options', 'Confirm customs services', 'Evaluate consolidation'],
          sentiment: 'positive',
          urgency: 'Medium'
        }
      }
    ]
  },
  {
    id: 'Q-004',
    subject: 'Cotización única - Cotización (00007023)',
    received: '2025-10-15T14:40:14',
    status: 'New',
    customerName: 'Mahe Neutral Shipping',
    company: 'Mahe Neutral Shipping',
    serviceType: 'Air',
    origin: 'New Delhi, Zip code 396191',
    destination: 'Bogotá, Colombia',
    cargoDescription: 'General cargo- Non haz',
    weight: '18 KGS',
    dimensions: '1 PCS (dimensions not specified)',
    specialRequirements: 'Incoterms: EXW. Quote needs to include Route, Transit Time (TT), Validity, Airline.',
    priority: 'Medium',
    assignedTo: 'Prashant Pathil',
    channel: 'Email',
    aiSummary: {
      leadName: 'Mahe Neutral Shipping',
      requirements: 'Air freight from New Delhi to Bogotá for general cargo weighing 18 kg',
      urgency: 'Medium',
      extractedFields: {
        product: 'General Cargo (Non-hazardous)',
        quantity: '1 piece',
        incoterms: 'EXW',
        timeline: 'Standard delivery'
      }
    }
  },
  {
    id: 'Q-005',
    subject: 'ENQ1403-UMA/INDIA TO DUBAI /AIR FREIGHT',
    received: '2025-10-15T12:59:12',
    status: 'New',
    customerName: 'Shiji S (Ms.)',
    company: 'Freightcare Logistics LLC',
    serviceType: 'Air Freight',
    origin: 'Pune, Maharashtra, India',
    destination: 'UAE',
    cargoDescription: 'Roller Track',
    weight: 'Not specified',
    dimensions: 'Not specified',
    specialRequirements: 'Quote on EXW basis',
    priority: 'Medium',
    assignedTo: 'Prashant Pathil',
    channel: 'WhatsApp',
    attachments: ['product-image.jpg'],
    aiSummary: {
      leadName: 'Shiji S',
      requirements: 'Air freight from Pune to Dubai for roller track equipment',
      urgency: 'Medium',
      extractedFields: {
        product: 'Roller Track',
        quantity: 'Not specified',
        incoterms: 'EXW',
        timeline: 'Standard delivery'
      }
    }
  },
  {
    id: 'Q-006',
    subject: 'SOLICITUD DE COTIZACIÓN // IMPO // 1 x 40" NOR // DALIAN - CALLAO',
    received: '2025-08-09T01:40:26',
    status: 'Quoted',
    customerName: 'Jheymy Alarcon',
    company: 'Seabridgelink',
    serviceType: 'Ocean',
    origin: 'DALIAN, China',
    destination: 'CALLAO',
    cargoDescription: 'LYSINE HCL FEED GRADE',
    weight: '27,000.00 Kg',
    dimensions: '1 x 40" NOR (Full Container Load)',
    specialRequirements: 'Cargo is ready for loading.',
    quotedRate: 2500,
    quotedTime: '2025-08-09T10:00:00',
    followUpDate: '2025-08-12T10:00:00',
    priority: 'Medium',
    assignedTo: 'Prashant Pathil',
    estimatedValue: 15000,
    channel: 'Email',
    aiSummary: {
      leadName: 'Jheymy Alarcon',
      requirements: 'Ocean freight from Dalian to Callao for 27,000 kg of Lysine HCL feed grade',
      urgency: 'Medium',
      extractedFields: {
        product: 'Lysine HCL Feed Grade',
        quantity: '27,000 kg',
        incoterms: 'FOB',
        timeline: 'Ready for loading'
      }
    },
    revisions: [
      {
        id: 'R-001',
        timestamp: '2025-08-09T10:00:00',
        changes: ['Initial quotation sent', 'Rate: $2,500'],
        updatedBy: 'Prashant Pathil',
        version: 1
      }
    ]
  },
  {
    id: 'Q-007',
    subject: 'SALCO LOGISTICS - AIR INQUIRY - URGENT ITEMS - HF',
    received: '2025-08-09T00:35:53',
    status: 'Acknowledged',
    customerName: 'SALCO Logistics',
    company: 'SALCO Logistics',
    serviceType: 'Air Freight',
    origin: 'Mumbai, Maharashtra, India',
    destination: 'Salvador, Brazil',
    cargoDescription: 'Details in attached image',
    weight: 'Not specified',
    dimensions: 'Not specified',
    specialRequirements: 'Incoterm: EXW. Request asks for specific quote details: Airline, Validity of the offer, Frequency, Flight schedule, International and Pick up Transit time, Minimum profit.',
    priority: 'High',
    assignedTo: 'Prashant Pathil',
    channel: 'Email',
    attachments: ['packing-details.jpg'],
    aiSummary: {
      leadName: 'SALCO Logistics',
      requirements: 'Urgent air freight from Mumbai to Salvador, Brazil with detailed specifications required',
      urgency: 'High',
      extractedFields: {
        product: 'General Cargo (details in image)',
        quantity: 'Not specified',
        incoterms: 'EXW',
        timeline: 'Urgent'
      }
    }
  },
  {
    id: 'Q-008',
    subject: 'AIR FREIGHT - LPO - 0631, 0609 and 0557 // QTN # : 186173',
    received: '2025-08-06T14:16:55',
    status: 'Won',
    customerName: 'Amalendhu',
    company: 'Customer Company',
    serviceType: 'Air Freight',
    origin: 'Mumbai, Maharashtra, India',
    destination: 'Dubai Airport',
    cargoDescription: '86 cartons of different sizes',
    weight: '801 kgs (Gross Weight)',
    dimensions: '86 cartons with varying dimensions',
    specialRequirements: 'Door pickup from shipper address in Mumbai to Dubai Airport.',
    quotedRate: 1800,
    quotedTime: '2025-08-06T16:00:00',
    priority: 'Medium',
    assignedTo: 'Prashant Pathil',
    estimatedValue: 12000,
    channel: 'Email',
    aiSummary: {
      leadName: 'Amalendhu',
      requirements: 'Air freight from Mumbai to Dubai for 86 cartons weighing 801 kg',
      urgency: 'Medium',
      extractedFields: {
        product: 'General Cargo (86 cartons)',
        quantity: '86 cartons',
        incoterms: 'EXW',
        timeline: 'Standard delivery'
      }
    },
    revisions: [
      {
        id: 'R-002',
        timestamp: '2025-08-06T16:00:00',
        changes: ['Initial quotation sent', 'Rate: $1,800'],
        updatedBy: 'Prashant Pathil',
        version: 1
      },
      {
        id: 'R-003',
        timestamp: '2025-08-07T10:00:00',
        changes: ['Customer accepted quotation', 'Deal closed'],
        updatedBy: 'Prashant Pathil',
        version: 2
      }
    ]
  }
];

export const dashboardMetrics: DashboardMetrics = {
  totalQuotations: 156,
  newQuotations: 23,
  quotedQuotations: 45,
  wonQuotations: 12,
  conversionRate: 26.7,
  avgResponseTime: '2.3 hours',
  totalValue: 185000,
  topChannels: [
    { channel: 'Email', count: 89 },
    { channel: 'WhatsApp', count: 34 },
    { channel: 'Phone', count: 23 },
    { channel: 'Web', count: 10 }
  ],
  recentActivity: [
    { id: 'Q-001', action: 'New quotation received', timestamp: '2025-10-22T13:18:56' },
    { id: 'Q-002', action: 'Quotation acknowledged', timestamp: '2025-10-21T19:56:57' },
    { id: 'Q-003', action: 'Quote sent to customer', timestamp: '2025-10-21T17:20:04' },
    { id: 'Q-004', action: 'Follow-up scheduled', timestamp: '2025-10-15T14:40:14' },
    { id: 'Q-005', action: 'Customer responded', timestamp: '2025-10-15T12:59:12' }
  ]
};